/**
 * Desktop Menu - Mega Menu Interactions
 * Gère l'ouverture/fermeture du mega-menu "GROUPE 3F" en desktop
 * Accessible au clavier (Tab, Escape, Enter, Space)
 */

export default class DesktopMenu {
  constructor() {
    this.init();
  }

  init() {
    // Vérifier si on est en desktop (>= 1024px)
    if (window.innerWidth < 1024) {
      return;
    }

    // Sélectionner tous les items de menu avec enfants (has-children)
    this.menuItems = document.querySelectorAll('.site-header__primary-nav .primary-nav__menu-item--has-children');

    if (!this.menuItems.length) {
      return;
    }

    this.menuItems.forEach(item => {
      const link = item.querySelector('.primary-nav__menu-link--level-1');
      const submenu = item.querySelector('.primary-nav__menu--level-2');

      if (!link || !submenu) return;

      // Créer le bouton chevron
      const chevronButton = this.createChevronButton(item, submenu);

      // Insérer le bouton chevron après le lien
      link.parentNode.insertBefore(chevronButton, link.nextSibling);

      // Click sur le lien parent = navigation normale (pas de toggle)
      // Le toggle se fait uniquement via le bouton chevron

      // Escape pour fermer
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-active')) {
          this.closeMegaMenu(item, submenu, chevronButton);
          chevronButton.focus(); // Retour focus sur le bouton chevron
        }
      });
    });

    // Fermer si clic en dehors (sans donner le focus au chevron)
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.primary-nav__menu-item--has-children')) {
        this.closeAllMegaMenus(false); // false = pas de focus sur le chevron
      }
    });

    // Gérer les clics sur les boutons fermer (delegation)
    document.addEventListener('click', (e) => {
      const closeButton = e.target.closest('[data-mega-menu-close]');
      if (closeButton) {
        e.preventDefault();
        e.stopPropagation();
        // Pas de focus sur le chevron lors d'un clic souris
        this.closeAllMegaMenus(false);
      }
    });

    // Gérer le resize window
    window.addEventListener('resize', () => {
      if (window.innerWidth < 1024) {
        this.closeAllMegaMenus();
      }
    });
  }

  /**
   * Créer un bouton chevron pour un lien avec sous-menu
   */
  createChevronButton(item, submenu) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'primary-nav__chevron-button';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', 'Toggle submenu');

    // SVG chevron
    button.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path d="M1 1L6 6L11 1" stroke="#432ABD" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    // Click sur le bouton chevron = toggle du mega-menu
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleMegaMenu(item, submenu, button);
    });

    // Enter ou Space sur le bouton chevron
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMegaMenu(item, submenu, button);
      }
    });

    // Focus : rendre le chevron visible
    button.addEventListener('focus', () => {
      button.classList.add('is-focused');
    });

    // Blur : masquer le chevron (sauf si menu ouvert)
    button.addEventListener('blur', () => {
      button.classList.remove('is-focused');
    });

    return button;
  }

  toggleMegaMenu(item, submenu, chevronButton) {
    const isOpen = item.classList.contains('is-active');

    // Fermer tous les autres menus d'abord
    this.closeAllMegaMenus();

    if (!isOpen) {
      this.openMegaMenu(item, submenu, chevronButton);
    } else {
      this.closeMegaMenu(item, submenu, chevronButton);
    }
  }

  openMegaMenu(item, submenu, chevronButton) {
    item.classList.add('is-active');
    submenu.setAttribute('aria-hidden', 'false');

    // Mettre à jour le bouton chevron
    if (chevronButton) {
      chevronButton.classList.add('is-active');
      chevronButton.setAttribute('aria-expanded', 'true');
    }

    // Fermer la recherche si ouverte
    const searchPanel = document.querySelector('[data-search-panel]');
    if (searchPanel && searchPanel.classList.contains('is-visible')) {
      searchPanel.classList.remove('is-visible');
      const searchToggle = document.querySelector('[data-search-toggle]');
      if (searchToggle) {
        searchToggle.setAttribute('aria-expanded', 'false');
      }
    }

    // Gérer le bouton fermer (dans le wrapper parent du submenu)
    const wrapper = submenu.closest('.mega-menu-wrapper');
    const closeButton = wrapper ? wrapper.querySelector('[data-mega-menu-close]') : null;
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Pas de focus sur le chevron lors d'un clic souris
        this.closeMegaMenu(item, submenu, chevronButton, false);
      });
    }

    // Focus trap : Tab boucle entre close button et liens du mega-menu
    this.setupMegaMenuFocusTrap(item, submenu);

    // Focus sur le premier lien de la liste à l'ouverture
    const firstLink = submenu.querySelector('.primary-nav__menu--level-2-links a[href]');
    if (firstLink) {
      setTimeout(() => firstLink.focus(), 100);
    }
  }

  setupMegaMenuFocusTrap(item, submenu) {
    if (!submenu) return;

    const wrapper = submenu.closest('.mega-menu-wrapper');
    const closeButton = wrapper ? wrapper.querySelector('[data-mega-menu-close]') : null;
    const submenuLinks = Array.from(submenu.querySelectorAll('a[href]'));

    // Tous les éléments focusables : bouton fermer + tous les liens
    const focusableElements = [closeButton, ...submenuLinks].filter(el => el);

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab' || !item.classList.contains('is-active')) return;

      // Shift+Tab sur premier élément → dernier
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
      // Tab sur dernier élément → premier
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    // Écouter Tab
    document.addEventListener('keydown', handleTabKey);

    // Nettoyer à la fermeture
    const cleanup = () => {
      document.removeEventListener('keydown', handleTabKey);
    };

    // Stocker pour cleanup
    this.megaMenuFocusTrapCleanup = cleanup;
  }

  closeMegaMenu(item, submenu, chevronButton, shouldFocus = true) {
    item.classList.remove('is-active');
    submenu.setAttribute('aria-hidden', 'true');

    // Ajouter classe pour désactiver le hover CSS
    item.classList.add('is-closing');

    // Retirer la classe après que la souris soit sortie
    const removeClosing = () => {
      item.classList.remove('is-closing');
      item.removeEventListener('mouseleave', removeClosing);
    };
    item.addEventListener('mouseleave', removeClosing);

    // Mettre à jour le bouton chevron
    if (chevronButton) {
      chevronButton.classList.remove('is-active');
      chevronButton.setAttribute('aria-expanded', 'false');

      // Focus seulement si demandé (navigation clavier)
      if (shouldFocus) {
        chevronButton.focus();
      }
    }

    // Nettoyer le focus trap
    if (this.megaMenuFocusTrapCleanup) {
      this.megaMenuFocusTrapCleanup();
      this.megaMenuFocusTrapCleanup = null;
    }
  }

  closeAllMegaMenus(shouldFocus = true) {
    if (!this.menuItems) return;

    this.menuItems.forEach(item => {
      const submenu = item.querySelector('.primary-nav__menu--level-2');
      const chevronButton = item.querySelector('.primary-nav__chevron-button');
      if (submenu) {
        this.closeMegaMenu(item, submenu, chevronButton, shouldFocus);
      }
    });
  }
}
